import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API, useAuth } from '../contexts/AuthContext';
import { alertarErro } from '../helpers/alerts';
import { baixarArquivo } from '../helpers/appNativo';

function formatNumero(numero) {
  if (!numero) return '';
  const limpo = String(numero).replace(/\D/g, '');
  if (limpo.length === 13) {
    return `+${limpo.slice(0, 2)} (${limpo.slice(2, 4)}) ${limpo.slice(4, 9)}-${limpo.slice(9)}`;
  }
  if (limpo.length === 12) {
    return `+${limpo.slice(0, 2)} (${limpo.slice(2, 4)}) ${limpo.slice(4, 8)}-${limpo.slice(8)}`;
  }
  return numero;
}

const FORM_VAZIO = {
  canal_id: '', numero: '', push_name: '', filial: '', setor: '',
  pular_fluxo: false, atendente_padrao: '',
};

// Como o histórico se lê em português. O banco guarda o nome técnico do campo
// porque rótulo muda e registro não pode mudar junto.
const ROTULO_CAMPO = {
  push_name: 'Nome',
  filial: 'Filial',
  setor_id: 'Fila padrão',
  pular_fluxo: 'Pular o bot',
  atendente_padrao: 'Atendente padrão',
};

// Quantos contatos vão por pedido na importação. 300 faz cada pedido fechar
// em poucos segundos (são duas idas ao banco por contato): longe o bastante
// do tempo em que o proxy corta a conexão, e sem virar uma enxurrada de
// pedidos. Ver importarArquivo.
const PEDACO_DA_IMPORTACAO = 300;

// De onde partiu a alteração. É o que responde "mas ninguém mexeu nisso" —
// quase sempre mexeu, por uma tela que não parecia mexer.
const ROTULO_ORIGEM = {
  fio: 'tela de contatos',
  conversa: 'salvar na agenda',
  arquivo: 'importação de arquivo',
  whatsapp: 'puxar do WhatsApp',
  automatico: 'automático',
  // Limpeza dos números com zero de discagem (scripts/consertar-numeros-com-zero.js):
  // o contato repetido foi juntado neste, e o nome que ele tinha ficou registrado aqui.
  fusao: 'junção de contato repetido',
  // Ficha criada porque o número LIGOU pro canal (ligacaoService).
  ligacao: 'ligação recebida',
};

function valorLegivel(linha, qual) {
  if (linha.campo === 'setor_id') {
    const nome = qual === 'antes' ? linha.setor_antes : linha.setor_depois;
    const cru = qual === 'antes' ? linha.valor_antes : linha.valor_depois;
    if (nome) return nome;
    return cru ? `fila #${cru}` : 'nenhuma';
  }
  const cru = qual === 'antes' ? linha.valor_antes : linha.valor_depois;
  if (linha.campo === 'pular_fluxo') return cru === '1' ? 'Sim' : (cru === '0' ? 'Não' : 'não definido');
  return cru || 'vazio';
}

export default function Contatos() {
  const { temPermissao } = useAuth();
  const podeUsar = temPermissao('ti:atendimentos');

  const [contatos, setContatos] = useState([]);
  const [canais, setCanais] = useState([]);
  const [filiais, setFiliais] = useState([]);
  const [setores, setSetores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [erro, setErro] = useState('');
  // A agenda tem milhares de fichas. Trazer tudo custava 2,4 MB e 4,5s de tela
  // parada só pra mostrar as primeiras vinte linhas — e ninguém rola 4 mil
  // contatos: procura. Então vem por página, com o total à vista pra ninguém
  // achar que a lista acabou onde ela só foi cortada.
  const [total, setTotal] = useState(null);
  const [carregandoMais, setCarregandoMais] = useState(false);
  const [importInfo, setImportInfo] = useState(null);

  // Tags: etiquetas livres, POR USUÁRIO (ver sql/096 — a 095 tinha feito por
  // canal e foi revertida). Quem marcou, vê: a lista e as marcações de um
  // atendente não aparecem pro outro, nem na mesma agenda.
  const [tags, setTags] = useState([]);
  const [tagsDoContato, setTagsDoContato] = useState([]);
  const [novaTag, setNovaTag] = useState('');
  const [tagOcupada, setTagOcupada] = useState(false);

  const carregarTags = async () => {
    try { setTags((await axios.get(`${API}/tags`)).data?.data || []); } catch (e) { setTags([]); }
  };
  useEffect(() => { if (podeUsar) carregarTags(); }, [podeUsar]);

  const alternarTag = async (tag) => {
    if (!editando) return;
    const marcada = tagsDoContato.some((t) => t.id === tag.id);
    setTagOcupada(true);
    try {
      const r = marcada
        ? await axios.delete(`${API}/contatos/${editando.id}/tags/${tag.id}`)
        : await axios.post(`${API}/contatos/${editando.id}/tags`, { tag_id: tag.id });
      setTagsDoContato(r.data?.data || []);
      await carregar();
    } catch (e) { alertarErro(e, 'Não deu pra mudar a tag'); }
    finally { setTagOcupada(false); }
  };

  // Criar e já aplicar: quem está com a ficha aberta quer etiquetar ESTE
  // contato — obrigar a ir num cadastro separado e voltar seria trabalho à toa.
  const criarTag = async () => {
    const nome = novaTag.trim();
    if (!nome || !editando) return;
    setTagOcupada(true);
    try {
      const r = await axios.post(`${API}/tags`, { nome });
      const criada = r.data?.data;
      setNovaTag('');
      await carregarTags();
      if (criada?.id) await alternarTag(criada);
    } catch (e) { alertarErro(e, 'Não deu pra criar a tag'); }
    finally { setTagOcupada(false); }
  };

  // Criar da tela de gerenciar: aqui não há contato aberto, então só cria — a
  // versão que cria E marca vive na ficha e na conversa, onde faz sentido.
  const criarTagSolta = async () => {
    const nome = novaTag.trim();
    if (!nome) return;
    setTagOcupada(true);
    try {
      await axios.post(`${API}/tags`, { nome });
      setNovaTag('');
      await carregarTags();
    } catch (e) { alertarErro(e, 'Não deu pra criar a tag'); }
    finally { setTagOcupada(false); }
  };

  const renomearTag = async (tag) => {
    const { value: nome } = await Swal.fire({
      title: 'Renomear tag', input: 'text', inputValue: tag.nome,
      showCancelButton: true, confirmButtonText: 'Salvar', cancelButtonText: 'Cancelar',
      confirmButtonColor: '#3D8B37',
    });
    if (!nome || nome === tag.nome) return;
    try { await axios.put(`${API}/tags/${tag.id}`, { nome }); await carregarTags(); await carregar(); }
    catch (e) { alertarErro(e, 'Não deu pra renomear'); }
  };

  const excluirTag = async (tag) => {
    const conf = await Swal.fire({
      icon: 'warning', title: `Excluir "${tag.nome}"?`,
      // Diz o estrago ANTES: tag usada em 40 contatos apagada por engano é
      // trabalho de gente perdido.
      text: tag.contatos > 0
        ? `Ela está em ${tag.contatos} contato(s) e sai de todos.`
        : 'Ela não está em nenhum contato.',
      showCancelButton: true, confirmButtonText: 'Excluir', cancelButtonText: 'Cancelar',
      confirmButtonColor: '#c0392b',
    });
    if (!conf.isConfirmed) return;
    try {
      await axios.delete(`${API}/tags/${tag.id}`);
      await carregarTags(); await carregar();
      if (editando) setTagsDoContato((atuais) => atuais.filter((t) => t.id !== tag.id));
    } catch (e) { alertarErro(e, 'Não deu pra excluir'); }
  };

  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [erroForm, setErroForm] = useState('');

  const [importAberto, setImportAberto] = useState(false);
  const [importando, setImportando] = useState(false);
  const [importResultado, setImportResultado] = useState(null);
  // Agenda é por canal: cada número/equipe tem a sua lista, com os seus nomes.
  // '' = todas as agendas que eu enxergo. `importCanal` = pra qual agenda importar.
  const [canalFiltro, setCanalFiltro] = useState('');
  // Filtrar por tag é o que faz marcar valer a pena: sem isso, a etiqueta é
  // enfeite. Vive junto do filtro de canal porque as duas perguntas são a
  // mesma — "me mostra só um pedaço da agenda".
  const [tagFiltro, setTagFiltro] = useState('');
  const [gerenciarTags, setGerenciarTags] = useState(false);
  const [importCanal, setImportCanal] = useState('');
  const [glossarioAberto, setGlossarioAberto] = useState(false);
  // Atendentes da fila escolhida no formulário (pro "atendente padrão").
  const [atendentesFila, setAtendentesFila] = useState([]);

  const POR_PAGINA = 200;

  const carregar = async (offset = 0) => {
    if (offset) setCarregandoMais(true); else setLoading(true);
    try {
      const [r1, r2, r3, r4] = await Promise.all([
        axios.get(`${API}/contatos`, {
          params: {
            limite: POR_PAGINA,
            ...(offset ? { offset } : {}),
            ...(busca ? { busca } : {}),
            ...(canalFiltro ? { canal_id: canalFiltro } : {}),
            ...(tagFiltro ? { tag_id: tagFiltro } : {}),
          },
        }),
        axios.get(`${API}/atendimento/canais`),
        axios.get(`${API}/atendimento/filiais`, { params: { ativos: 'true' } }).catch(() => ({ data: { data: [] } })),
        axios.get(`${API}/setores`, { params: { ativos: 'true' } }).catch(() => ({ data: { data: [] } })),
      ]);
      const pagina = r1.data?.data || [];
      // O total vem na própria linha (COUNT(*) OVER no SQL): sem consulta extra.
      setTotal(pagina[0]?.total_geral ?? (offset ? total : pagina.length));
      setContatos((atuais) => (offset ? [...atuais, ...pagina] : pagina));
      setCanais(r2.data?.data || []);
      setFiliais(r3.data?.data || []);
      setSetores(r4.data?.data || []);
      setErro('');
    } catch (e) {
      setErro(e.response?.data?.message || 'Não foi possível carregar os dados.');
    } finally {
      setLoading(false);
      setCarregandoMais(false);
    }
  };

  // Puxa a agenda do próprio WhatsApp (aparelho conectado) pra agenda do canal.
  const puxarDoWhatsApp = async () => {
    const canalNome = canais.find((c) => String(c.id) === String(importCanal))?.nome || 'do canal';
    const r = await Swal.fire({
      icon: 'question',
      title: 'Puxar contatos do WhatsApp?',
      html: `Traz os contatos <b>com nome salvo</b> que o aparelho conectado conhece`
        + ` para a agenda de <b>${canalNome}</b>.<br><span style="font-size:0.85rem;opacity:0.8">`
        + `Não sobrescreve nome que já existe aqui.</span>`,
      showCancelButton: true,
      confirmButtonText: 'Puxar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#3D8B37',
    });
    if (!r.isConfirmed) return;
    setImportando(true);
    setImportResultado(null);
    try {
      const res = await axios.post(`${API}/contatos/import-whatsapp`, {
        ...(importCanal ? { canal_id: Number(importCanal) } : {}),
      });
      setImportResultado(res.data?.data || null);
      await carregar();
    } catch (e) {
      alertarErro(e, 'Não deu pra puxar os contatos');
    } finally {
      setImportando(false);
    }
  };

  // Fio -> celular: o WhatsApp não deixa escrever na agenda do aparelho por
  // API, então o alinhamento é exportar daqui e importar lá, de uma vez.
  const exportarAgenda = async () => {
    const canalNome = canais.find((c) => String(c.id) === String(importCanal))?.nome || 'do canal';
    // Duas intenções diferentes: alinhar o celular do zero (tudo) ou levar só
    // o que nasceu aqui e ainda não existe no aparelho.
    const { value: escopo, isConfirmed } = await Swal.fire({
      title: 'Exportar pro celular',
      html: `<div style="text-align:left;font-size:0.88rem">Agenda de <b>${canalNome}</b>.</div>`,
      input: 'radio',
      inputOptions: {
        novos: 'Só o que ainda não foi pro celular (recomendado)',
        fio_todos: 'Todos os salvos no Fio — mesmo os já exportados',
        tudo: 'A agenda inteira deste canal',
      },
      inputValue: 'novos',
      showCancelButton: true,
      confirmButtonText: 'Baixar .vcf',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#3D8B37',
    });
    if (!isConfirmed || !escopo) return;
    try {
      const res = await axios.get(`${API}/contatos/exportar-vcf`, {
        params: { canal_id: importCanal || undefined, somente: escopo },
        responseType: 'blob',
      });
      // Conta pelo conteúdo, não pelo header: header customizado depende de
      // CORS (exposedHeaders) e some sem avisar. O arquivo em mãos não mente.
      const texto = await res.data.text();
      const total = (texto.match(/BEGIN:VCARD/g) || []).length;
      if (!total) {
        Swal.fire({
          icon: 'info',
          title: 'Nada pra exportar',
          text: escopo === 'novos'
            ? 'Tudo que foi salvo pelo Fio já foi pro celular. Salve um contato novo por aqui e exporte de novo.'
            : (escopo === 'fio_todos'
              ? 'Nenhum contato foi cadastrado pelo Fio nesta agenda ainda.'
              : 'Esta agenda não tem contato com nome.'),
        });
        return;
      }
      const nomeArquivo = `agenda-fio-${String(canalNome).toLowerCase().replace(/\s+/g, '-')}${escopo === 'tudo' ? '' : '-novos'}.vcf`;
      await baixarArquivo(nomeArquivo, new Blob([texto], { type: 'text/vcard;charset=utf-8' }));
      // A ficha só sai da próxima exportação quando VOCÊ confirma que importou.
      // Gerar o arquivo não garante que ele chegou no aparelho.
      const conf = await Swal.fire({
        icon: 'success',
        title: `${total} contato(s) no arquivo`,
        html: 'Abra o <b>.vcf</b> no celular pra importar.<br>'
          + '<span style="font-size:0.85rem">iPhone: toque no arquivo → <b>Adicionar todos</b> → '
          + '<b>Mesclar</b>. Android: Contatos → Configurações → <b>Importar</b> → .vcf.</span>'
          + '<br><br><span style="font-size:0.85rem">Depois de importar, confirme aqui pra estes '
          + 'não virem na próxima exportação.</span>',
        showCancelButton: true,
        confirmButtonText: 'Já importei no celular',
        cancelButtonText: 'Ainda não',
        confirmButtonColor: '#3D8B37',
      });
      if (conf.isConfirmed && escopo !== 'tudo') {
        try {
          await axios.post(`${API}/contatos/marcar-enviados`, {
            canal_id: importCanal || undefined,
          });
          await carregar();
        } catch (e) {
          alertarErro(e, 'Não deu pra confirmar');
        }
      }
    } catch (e) {
      alertarErro(e, 'Não deu pra exportar');
    }
  };

  // A importação vai em DUAS etapas: primeiro o servidor lê o arquivo e devolve
  // a lista pronta (rápido, sem gravar nada), depois a tela manda essa lista em
  // pedaços.
  //
  // Num pedido só, 786 contatos levavam ~7 minutos e o proxy cortava a conexão
  // antes do fim: a importação da Bruna (17/09) gravou os 786 e mesmo assim a
  // tela disse "conexão falha", porque só a resposta se perdeu. Em pedaços dá
  // pra mostrar progresso e, se cair no meio, o que já entrou fica.
  const importarArquivo = async (file) => {
    if (!file) return;
    setImportando(true);
    setImportResultado(null);
    let gravados = 0;
    try {
      const conteudo = await file.text();
      const formato = /\.vcf$/i.test(file.name)
        ? 'vcard'
        : (/\.csv$/i.test(file.name) ? 'csv' : undefined);
      // Importa PARA a agenda escolhida — a mesma lista pode entrar em dois
      // canais, cada um com os seus nomes, sem uma atropelar a outra.
      const canal = importCanal ? { canal_id: Number(importCanal) } : {};
      const kb = Math.round(file.size / 1024);
      setImportInfo({ fase: 'lendo', feitos: 0, total: 0, kb });

      const prep = (await axios.post(`${API}/contatos/import/preparar`, { conteudo, formato, ...canal }))
        .data?.data || {};
      const itens = prep.itens || [];
      setImportInfo({ fase: 'gravando', feitos: 0, total: itens.length, kb });

      const soma = { criados: 0, atualizados: 0, ja_existia: 0, erro: 0 };
      const exemplos_erro = [];
      const ids = {};
      for (let i = 0; i < itens.length; i += PEDACO_DA_IMPORTACAO) {
        const pedaco = itens.slice(i, i + PEDACO_DA_IMPORTACAO);
        const mandar = () => axios.post(`${API}/contatos/import`, { itens: pedaco, ...canal });
        // Uma segunda tentativa por pedaço: a loja troca de wi-fi pra 4G no
        // meio de uma importação de minutos, e derrubar tudo por um tropeço de
        // conexão seria o problema antigo de volta, só que mais tarde.
        // eslint-disable-next-line no-await-in-loop
        const res = await mandar().catch(() => mandar());
        const r = res.data?.data || {};
        soma.criados += r.criados || 0;
        soma.atualizados += r.atualizados || 0;
        soma.ja_existia += r.motivos?.ja_existia || 0;
        soma.erro += r.motivos?.erro || 0;
        if (Array.isArray(r.exemplos_erro)) {
          exemplos_erro.push(...r.exemplos_erro.slice(0, Math.max(0, 20 - exemplos_erro.length)));
        }
        Object.assign(ids, r.ids || {});
        // `feitos` é const desta volta do laço: a função que atualiza a tela
        // não pode fechar em cima de uma variável que o laço ainda vai mexer.
        const feitos = Math.min(i + PEDACO_DA_IMPORTACAO, itens.length);
        gravados = feitos;
        setImportInfo((info) => (info ? { ...info, feitos } : info));
      }

      const motivos = { ...(prep.motivos || {}), ja_existia: soma.ja_existia, erro: soma.erro };
      setImportResultado({
        total: prep.total || 0,
        criados: soma.criados,
        atualizados: soma.atualizados,
        // Todo cartão do arquivo cai em exatamente um balde, então a soma dos
        // motivos É o ignorados — é o que faz a conta fechar com o total lido.
        ignorados: Object.values(motivos).reduce((a, b) => a + b, 0),
        motivos,
        exemplos_invalidos: prep.exemplos_invalidos || [],
        exemplos_erro,
        // O id de cada contato só existe depois de gravar, e é ele que deixa
        // escolher o nome certo num clique.
        nomes_disputados: (prep.nomes_disputados || [])
          .map((d) => ({ ...d, contato_id: ids[d.numero] ?? null })),
      });
      await carregar();
    } catch (e) {
      // Cair no meio não joga mais fora o que já entrou: subir o mesmo arquivo
      // de novo completa o que falta (quem já está na agenda é contado como
      // "já existia", não duplicado).
      alertarErro(e, gravados
        ? `Parou no meio — ${gravados.toLocaleString('pt-BR')} contato(s) já foram salvos`
        : 'Não deu pra importar');
      if (gravados) await carregar();
    } finally {
      setImportando(false);
      setImportInfo(null);
    }
  };

  // Modal aberta: trava a rolagem do fundo e liga o ESC. Sem travar o fundo, a
  // roda do mouse sobre a área escura rolava a LISTA de contatos atrás do modal.
  const algumaModalAberta = modalAberto || importAberto;
  useEffect(() => {
    if (!algumaModalAberta) return undefined;
    const overflowAntes = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (salvando || importando) return;
      setModalAberto(false);
      setImportAberto(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = overflowAntes;
      window.removeEventListener('keydown', onKey);
    };
  }, [algumaModalAberta, salvando, importando]);

  useEffect(() => {
    if (!podeUsar) { setLoading(false); return; }
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [podeUsar, busca, canalFiltro, tagFiltro]);

  // Filas do canal escolhido no formulário — fila pertence a um canal, então a
  // lista muda conforme a agenda (canal) em que o contato está.
  const filasDoCanal = setores.filter(
    (f) => !form.canal_id || f.canal_id == null || Number(f.canal_id) === Number(form.canal_id),
  );

  // Atendentes da fila escolhida — alimenta o "atendente padrão" do contato.
  useEffect(() => {
    const fila = filasDoCanal.find((f) => f.nome === form.setor);
    if (!modalAberto || !fila) { setAtendentesFila([]); return; }
    axios.get(`${API}/setores/${fila.id}/atendentes`)
      .then((r) => setAtendentesFila(r.data?.data || []))
      .catch(() => setAtendentesFila([]));
  }, [modalAberto, form.setor, setores]);

  // Import cai na agenda filtrada; sem filtro, no primeiro canal que eu vejo.
  useEffect(() => {
    setImportCanal((atual) => atual || canalFiltro || canais[0]?.id || '');
  }, [canais, canalFiltro]);

  const abrirNovo = () => {
    setEditando(null);
    // Já vem na agenda que está sendo olhada (evita cadastrar na lista errada).
    setForm({ ...FORM_VAZIO, canal_id: canalFiltro || canais[0]?.id || '' });
    setErroForm('');
    setModalAberto(true);
  };

  // Histórico da ficha: quem mexeu na fila, no nome, no "pular o bot" e por
  // qual tela. Só carrega quando alguém pede — é consulta de investigação, não
  // informação de todo dia.
  const [historico, setHistorico] = useState(null);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);

  const verHistorico = async (contato) => {
    setCarregandoHistorico(true);
    try {
      const r = await axios.get(`${API}/contatos/${contato.id}/historico`, {
        params: { canal_id: contato.canal_id },
      });
      setHistorico(r.data?.data || []);
    } catch (e) {
      setHistorico([]);
    } finally {
      setCarregandoHistorico(false);
    }
  };

  // Escolher qual nome vale, entre os que vieram repetidos no arquivo. Some da
  // lista ao aplicar: o que sobra é o que ainda falta decidir.
  const [nomeEscolhido, setNomeEscolhido] = useState({});
  const escolherNome = async (d, nome) => {
    if (!d.contato_id) return;
    setNomeEscolhido((m) => ({ ...m, [d.numero]: 'salvando' }));
    try {
      await axios.put(`${API}/contatos/${d.contato_id}`, {
        canal_id: Number(importCanal) || undefined,
        push_name: nome,
      });
      setNomeEscolhido((m) => ({ ...m, [d.numero]: nome }));
      await carregar();
    } catch (e) {
      setNomeEscolhido((m) => ({ ...m, [d.numero]: undefined }));
      alertarErro(e, 'Não deu pra trocar o nome');
    }
  };

  const abrirEditar = (contato) => {
    setEditando(contato);
    setForm({
      canal_id: contato.canal_id,
      numero: contato.numero || '',
      push_name: contato.push_name || '',
      filial: contato.filial || '',
      setor: contato.setor || '',
      pular_fluxo: !!contato.pular_fluxo,
      atendente_padrao: contato.atendente_padrao || '',
    });
    setErroForm('');
    setHistorico(null);
    setTagsDoContato([]);
    setNovaTag('');
    axios.get(`${API}/contatos/${contato.id}/tags`)
      .then((r) => setTagsDoContato(r.data?.data || []))
      .catch(() => setTagsDoContato([]));
    setModalAberto(true);
  };

  const salvar = async () => {
    if (!editando && !form.numero) { setErroForm('Informe o número.'); return; }
    const dados = {
      push_name: form.push_name.trim(),
      filial: form.filial.trim() || null,
      setor: form.setor.trim() || null,
    };
    if (!dados.push_name) { setErroForm('Nome é obrigatório.'); return; }
    setSalvando(true);
    setErroForm('');
    try {
      if (editando) {
        // Edita a ficha DAQUELA agenda — o nome do outro canal não é tocado.
        await axios.put(`${API}/contatos/${editando.id}`, {
          canal_id: Number(editando.canal_id),
          push_name: dados.push_name,
          filial: dados.filial,
          setor: dados.setor,
          pular_fluxo: form.pular_fluxo,
          atendente_padrao: form.atendente_padrao || null,
        });
      } else {
        await axios.post(`${API}/contatos`, {
          canal_id: form.canal_id ? Number(form.canal_id) : null,
          numero: form.numero,
          push_name: dados.push_name,
          filial: dados.filial,
          setor: dados.setor,
          pular_fluxo: form.pular_fluxo,
          atendente_padrao: form.atendente_padrao || null,
        });
      }
      setModalAberto(false);
      await carregar();
    } catch (e) {
      setErroForm(e.response?.data?.message || 'Não foi possível salvar.');
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (contato) => {
    const nomeCanal = canais.find((k) => k.id === contato.canal_id)?.nome || 'deste canal';
    const ok = await Swal.fire({
      icon: 'warning',
      title: 'Tirar da agenda?',
      text: `${contato.push_name || formatNumero(contato.numero)} sai da agenda de ${nomeCanal}. As conversas continuam no histórico, e a agenda dos outros canais não muda.`,
      showCancelButton: true,
      confirmButtonText: 'Tirar da agenda',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#b43737',
    });
    if (!ok.isConfirmed) return;
    try {
      await axios.delete(`${API}/contatos/${contato.id}`, { params: { canal_id: contato.canal_id } });
      await carregar();
    } catch (e) {
      alertarErro(e, 'Não deu pra excluir');
    }
  };

  if (!podeUsar) {
    return (
      <div className="content-shell">
        <div className="feedback-banner feedback-banner--error">
          Você não tem permissão para acessar contatos.
        </div>
      </div>
    );
  }

  return (
    <div className="content-shell">
      <section className="page-header">
        <div>
          <span className="section-header__eyebrow">FIO</span>
          <h1>Contatos (agenda)</h1>
          <p>
            Cada <b>canal</b> (número de WhatsApp da loja) tem a <b>sua agenda</b>: o mesmo
            cliente pode estar salvo com nomes diferentes em cada uma, sem misturar.
            Aqui você também define pra onde a mensagem dele vai — <b>fila</b> (setor) ou
            <b>atendente</b> específico.
            <br />
            <b>Salve os contatos sempre por aqui</b> (ou pelo cartão de contato da conversa) —
            salvar no celular não traz o nome pro Fio. Pra levar os nomes ao aparelho, use
            <b> Exportar</b>.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <a
            href="/ti/atendimentos"
            className="btn btn-sm btn-outline-secondary"
            title="Voltar para a lista de conversas"
          >
            <i className="bi bi-chat-left-text me-1" /> Conversas
          </a>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => { setImportResultado(null); setImportAberto(true); }}
            title="Trazer contatos de um arquivo CSV/vCard ou do WhatsApp"
          >
            <i className="bi bi-upload me-1" /> Importar
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setGerenciarTags(true)}
            title="Criar, renomear e excluir suas tags"
          >
            <i className="bi bi-tags me-1" /> Minhas tags
            {tags.length > 0 && (
              <span style={{
                marginLeft: 5, background: 'var(--portal-surface-soft)', borderRadius: 8,
                padding: '0 6px', fontSize: '0.72rem',
              }}>{tags.length}</span>
            )}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={exportarAgenda}
            title="Baixa em .vcf pra importar no celular — por padrão só o que ainda não foi"
          >
            <i className="bi bi-download me-1" /> Exportar
          </button>
          <button
            type="button"
            className="btn btn-sm"
            onClick={abrirNovo}
            style={{ background: '#3D8B37', color: '#fff', border: 'none' }}
          >
            <i className="bi bi-plus-lg me-1" /> Novo contato
          </button>
        </div>
      </section>

      <div className="filter-card">
        <div className="filter-card__field" style={{ flex: 1 }}>
          <input
            type="search"
            placeholder="Buscar por nome ou número..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        {canais.length > 1 && (
          <div className="filter-card__field">
            <label style={{ fontSize: '0.72rem', color: 'var(--portal-muted)' }}>
              Agenda do canal
            </label>
            <select
              className="form-select form-select-sm"
              value={canalFiltro}
              onChange={(e) => setCanalFiltro(e.target.value)}
              title="Cada canal (número) tem a sua agenda de contatos"
            >
              <option value="">Todas que eu vejo</option>
              {canais.map((c) => (
                <option key={c.id} value={c.id}>{c.nome}</option>
              ))}
            </select>
          </div>
        )}
        {tags.length > 0 && (
          <div className="filter-card__field">
            <label style={{ fontSize: '0.72rem', color: 'var(--portal-muted)' }}>
              Minha tag
            </label>
            <select
              className="form-select form-select-sm"
              value={tagFiltro}
              onChange={(e) => setTagFiltro(e.target.value)}
              title="Mostrar só os contatos que eu marquei com esta tag"
            >
              <option value="">Todas</option>
              {tags.filter((t) => t.ativo).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nome}{t.contatos ? ` (${t.contatos})` : ''}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="filter-card__field" style={{ alignSelf: 'flex-end' }}>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setGlossarioAberto((v) => !v)}
            title="O que significa canal, fila e atendente"
          >
            <i className="bi bi-info-circle me-1" />O que é o quê
          </button>
        </div>
      </div>

      {glossarioAberto && (
        <div className="content-card" style={{ padding: 14, marginBottom: 12 }}>
          <div style={{ display: 'grid', gap: 8, fontSize: '0.85rem', lineHeight: 1.5 }}>
            {[
              ['COMO SALVAR (a regra)', 'Salve o contato SEMPRE aqui no Fio — nesta tela ou pelo botão Salvar no cartão de contato que o cliente manda na conversa. Não salve no celular: o WhatsApp não avisa o Fio, e as duas agendas começam a divergir.'],
              ['E o celular?', 'O nome só aparece no WhatsApp depois que o contato entra na agenda do aparelho. Use Exportar (padrão: só o que ainda não foi) e importe o .vcf no celular. Serve pra quando precisar — trocar de aparelho, alinhar as pontas, ou quando quiser ver os nomes lá.'],
              ['Canal', 'Um número de WhatsApp da loja (ex.: o número do Crediário). Cada canal tem a sua agenda de contatos e os seus atendentes.'],
              ['Agenda', 'A lista de contatos daquele canal. O mesmo cliente pode estar em duas agendas com nomes diferentes — cada equipe salva do jeito dela e a busca de uma não atrapalha a da outra.'],
              ['Fila (setor)', 'O time/assunto dentro do canal: Caixa, Crediário, Vendas. É o que decide quem enxerga a conversa.'],
              ['Atendente', 'A pessoa que responde (usuário do Fio).'],
              ['Fila padrão do contato', 'Pra qual fila as mensagens desse cliente vão quando ele fala nesse canal. É o nível mais amplo.'],
              ['Atendente padrão do contato', 'Cai direto nessa pessoa, sem passar pela fila comum. É o nível mais específico — use pro cliente que é sempre do mesmo vendedor.'],
              ['Pular o bot', 'Esse cliente não vê o menu de escolha; vai direto pelo roteamento configurado acima.'],
              ['Onde salvar contato', 'Salve pelo Fio: aqui, ou no cartão de contato que o cliente manda na conversa. Salvar no celular NÃO traz o nome pra cá (o WhatsApp não compartilha a agenda do aparelho). Pro caminho contrário, "Exportar pro celular" baixa a agenda em .vcf.'],
            ].map(([termo, texto]) => (
              <div key={termo} style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 22%) 1fr', gap: 10 }}>
                <div style={{ fontWeight: 700 }}>{termo}</div>
                <div style={{ color: 'var(--portal-muted)' }}>{texto}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {erro && <div className="feedback-banner feedback-banner--error">{erro}</div>}

      <section className="content-card">
        {loading ? (
          <div className="content-card--centered">
            <div className="screen-loader__spinner" />
            <p>Carregando os primeiros 200 contatos...</p>
          </div>
        ) : contatos.length === 0 ? (
          <div className="empty-state">
            <strong>Nenhum contato cadastrado</strong>
            <span>Clique em "Novo contato" para começar.</span>
          </div>
        ) : (
          <div className="table-shell">
            <table className="portal-table">
              <thead>
                <tr>
                  <th>Nome (nesta agenda)</th>
                  <th>Número</th>
                  <th>Filial</th>
                  <th title="Pra qual fila as mensagens desse cliente vão">Fila padrão</th>
                  <th title="Cai direto nessa pessoa, sem passar pela fila">Atendente padrão</th>
                  <th title="Qual número/agenda da loja">Canal</th>
                  <th style={{ textAlign: 'right' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {contatos.map((c) => {
                  const canal = canais.find((k) => k.id === c.canal_id);
                  // Bot so faz intake quando falta NOME; filial/setor sao
                  // preenchidos pelo atendente e nao disparam o alerta.
                  const semNome = !c.push_name;
                  return (
                    <tr
                      key={`${c.id}-${c.canal_id}`}
                      // A linha inteira abre a ficha. Mirar no lápis é trabalho
                      // à toa — no tablet então, onde o alvo é minúsculo.
                      onClick={() => abrirEditar(c)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <span className="cell-strong">
                          {c.push_name || <span className="cell-muted">(sem nome)</span>}
                        </span>
                        {Array.isArray(c.tags) && c.tags.map((t) => (
                          <span key={t.id} title="Tag" style={{
                            marginLeft: 6, fontSize: '0.65rem', fontWeight: 700, padding: '1px 7px',
                            borderRadius: 8, color: '#fff', background: t.cor || '#3D8B37',
                          }}>{t.nome}</span>
                        ))}
                        {semNome && (
                          <span
                            title="Contato sem nome - bot fará a identificação na próxima conversa"
                            style={{
                              marginLeft: 6, fontSize: '0.7rem',
                              color: '#F5A623', fontWeight: 600,
                            }}
                          >
                            <i className="bi bi-exclamation-triangle" />
                          </span>
                        )}
                      </td>
                      {/* Marca o número que o WhatsApp já recusou, pra ninguém
                          tentar escrever de novo (migração 088). */}
                      <td className="cell-muted">
                        {formatNumero(c.numero)}
                        {c.sem_whatsapp ? (
                          <span
                            title="O WhatsApp não reconhece esse número — não dá pra enviar mensagem"
                            style={{ marginLeft: 6, fontSize: '0.7rem', color: '#dc3545', fontWeight: 600 }}
                          >
                            <i className="bi bi-x-octagon-fill me-1" />sem WhatsApp
                          </span>
                        ) : null}
                      </td>
                      <td className="cell-muted">{c.filial || '-'}</td>
                      <td className="cell-muted">
                        {c.setor || '-'}
                        {c.pular_fluxo && (
                          <span
                            title="Não passa pelo menu do bot"
                            style={{
                              marginLeft: 6, fontSize: '0.65rem', fontWeight: 700,
                              color: '#F5A623', border: '1px solid rgba(245,166,35,0.5)',
                              background: 'rgba(245,166,35,0.14)', borderRadius: 8, padding: '1px 6px',
                            }}
                          >
                            sem bot
                          </span>
                        )}
                      </td>
                      <td className="cell-muted">{c.atendente_padrao || '-'}</td>
                      <td className="cell-muted">{canal?.nome || (c.canal_id ? `#${c.canal_id}` : '-')}</td>
                      {/* `stopPropagation` aqui não é ritual: sem ele, Excluir
                          abriria a ficha por baixo do alerta de confirmação. */}
                      <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary me-1"
                          onClick={() => abrirEditar(c)}
                          title="Editar"
                        >
                          <i className="bi bi-pencil" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => excluir(c)}
                          title="Excluir contato"
                        >
                          <i className="bi bi-trash" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {/* Quantos de quantos. Sem isto, uma lista cortada em 200 parece a
                agenda inteira — e some gente sem ninguém perceber. */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 12, padding: '10px 4px', fontSize: '0.8rem', color: 'var(--portal-muted)',
            }}>
              <span>
                Mostrando <b>{contatos.length}</b>
                {total != null && total > contatos.length ? <> de <b>{total.toLocaleString('pt-BR')}</b></> : null}
                {' '}contato{contatos.length === 1 ? '' : 's'}
                {total != null && total > contatos.length ? ' — use a busca pra achar alguém específico' : ''}
              </span>
              {total != null && contatos.length < total && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={carregandoMais}
                  onClick={() => carregar(contatos.length)}
                >
                  {carregandoMais ? 'Carregando…' : `Carregar mais ${Math.min(200, total - contatos.length)}`}
                </button>
              )}
            </div>
          </div>
        )}
      </section>

      {/* MINHAS TAGS — lugar próprio pra gerenciar. Gerenciar vinte etiquetas
          abrindo um contato qualquer era o que faltava: a marcação acontece na
          conversa, mas arrumar a lista precisa de uma tela. */}
      {gerenciarTags && (
        <div
          onClick={() => setGerenciarTags(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(8, 23, 43, 0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <div className="content-card" onClick={(e) => e.stopPropagation()}
            style={{ width: 'min(520px, 96vw)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: 0 }}>
            <header style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 18px', borderBottom: '1px solid var(--portal-border)',
            }}>
              <strong><i className="bi bi-tags me-2" />Minhas tags</strong>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setGerenciarTags(false)}>
                <i className="bi bi-x-lg" />
              </button>
            </header>

            <div style={{ padding: 16, overflowY: 'auto' }}>
              <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
                <input
                  className="form-control form-control-sm"
                  placeholder="Nome da tag nova"
                  value={novaTag}
                  maxLength={40}
                  onChange={(e) => setNovaTag(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); criarTagSolta(); } }}
                />
                <button type="button" className="btn btn-sm"
                  style={{ background: '#3D8B37', color: '#fff', border: 'none', whiteSpace: 'nowrap' }}
                  disabled={!novaTag.trim() || tagOcupada} onClick={criarTagSolta}>
                  Criar
                </button>
              </div>

              {tags.length === 0 ? (
                <div className="empty-state">
                  <strong>Nenhuma tag ainda</strong>
                  <span>Crie a primeira acima, ou direto na conversa pelo botão Tags.</span>
                </div>
              ) : (
                <div style={{ border: '1px solid var(--portal-border)', borderRadius: 10, overflow: 'hidden' }}>
                  {tags.map((t) => (
                    <div key={t.id} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                      borderBottom: '1px solid var(--portal-border)',
                    }}>
                      {/* A cor é o que faz bater o olho na lista, então se
                          troca aqui mesmo, sem abrir outra coisa. */}
                      <input
                        type="color"
                        value={t.cor || '#3D8B37'}
                        onChange={async (e) => {
                          try { await axios.put(`${API}/tags/${t.id}`, { cor: e.target.value }); await carregarTags(); await carregar(); }
                          catch (err) { alertarErro(err, 'Não deu pra mudar a cor'); }
                        }}
                        title="Cor da tag"
                        style={{ width: 30, height: 26, padding: 0, border: '1px solid var(--portal-border)', borderRadius: 6, background: 'none', cursor: 'pointer' }}
                      />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <strong style={{ fontSize: '0.92rem' }}>{t.nome}</strong>
                        <div style={{ fontSize: '0.74rem', color: 'var(--portal-muted)' }}>
                          {t.contatos === 0 ? 'nenhum contato' : `${t.contatos} contato(s)`}
                        </div>
                      </span>
                      {t.contatos > 0 && (
                        <button type="button" className="btn btn-sm btn-outline-secondary"
                          onClick={() => { setTagFiltro(String(t.id)); setGerenciarTags(false); }}
                          title="Ver os contatos com esta tag">
                          <i className="bi bi-funnel" />
                        </button>
                      )}
                      <button type="button" className="btn btn-sm btn-outline-secondary"
                        onClick={() => renomearTag(t)} title="Renomear">
                        <i className="bi bi-pencil" />
                      </button>
                      <button type="button" className="btn btn-sm btn-outline-danger"
                        onClick={() => excluirTag(t)} title="Excluir">
                        <i className="bi bi-trash" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ fontSize: '0.76rem', color: 'var(--portal-muted)', marginTop: 12 }}>
                As tags são <b>suas</b>: outro atendente não vê as que você cria nem as marcações que você faz.
              </div>
            </div>
          </div>
        </div>
      )}

      {importAberto && (
        <div
          onClick={() => { if (!importando) setImportAberto(false); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(8, 23, 43, 0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <div
            className="content-card"
            onClick={(e) => e.stopPropagation()}
            style={{ width: 'min(560px, 96vw)', maxHeight: '92vh', overflowY: 'auto', padding: 0, display: 'flex', flexDirection: 'column' }}
          >
            <header style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 18px', borderBottom: '1px solid var(--portal-border)',
            }}>
              <strong><i className="bi bi-upload me-2" />Importar contatos</strong>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setImportAberto(false)}>
                <i className="bi bi-x-lg" />
              </button>
            </header>

            <div style={{ padding: 18 }}>
              <p style={{ color: 'var(--portal-muted)', fontSize: '0.85rem', marginTop: 0 }}>
                A importação entra na <b>agenda do canal escolhido</b> abaixo. A mesma lista pode
                ser importada em mais de um canal — cada um fica com os seus nomes.
                <br />
                <b>Importante:</b> salvar um contato no celular <b>não</b> traz o nome pra cá — o
                WhatsApp não compartilha a agenda do aparelho. Salve pelo Fio (aqui ou no cartão de
                contato da conversa) e, se quiser a mesma lista no celular, use
                <b> Exportar pro celular</b>.
                <br />
                A importação traz os <b>nomes e números</b> da sua agenda. Não duplica: números que já existem
                são reconhecidos pelo número. A <b>foto</b> vem do WhatsApp conforme a pessoa conversa.
              </p>

              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                <div style={{ flex: 1, minWidth: 220, padding: 12, borderRadius: 10, border: '1px solid var(--portal-border)' }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}><i className="bi bi-android2 me-2" />Android (Google Contatos)</div>
                  <ol style={{ margin: 0, paddingLeft: 18, fontSize: '0.82rem', color: 'var(--portal-muted)', lineHeight: 1.6 }}>
                    <li>No PC, abra <b>contacts.google.com</b>.</li>
                    <li>Menu <b>Exportar</b> → formato <b>Google CSV</b>.</li>
                    <li>Suba o arquivo <b>.csv</b> aqui.</li>
                  </ol>
                </div>
                <div style={{ flex: 1, minWidth: 220, padding: 12, borderRadius: 10, border: '1px solid var(--portal-border)' }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}><i className="bi bi-apple me-2" />iPhone (vCard)</div>
                  <ol style={{ margin: 0, paddingLeft: 18, fontSize: '0.82rem', color: 'var(--portal-muted)', lineHeight: 1.6 }}>
                    <li>Abra o app <b>Contatos</b> no iPhone.</li>
                    <li>Toque em <b>Listas</b> (canto superior esquerdo).</li>
                    <li>Segure sobre uma lista → <b>Exportar</b> → escolha os campos → <b>Exportar</b>.</li>
                    <li>Salve/envie o <b>.vcf</b> (Mail, Arquivos…) e suba aqui.</li>
                  </ol>
                </div>
              </div>

              <div style={{ marginBottom: 12, maxWidth: 340 }}>
                <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                  Importar para a agenda do canal
                </label>
                <select
                  className="form-select"
                  value={importCanal}
                  onChange={(e) => setImportCanal(e.target.value)}
                  disabled={importando}
                >
                  {canais.map((c) => (
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={puxarDoWhatsApp}
                disabled={importando}
                title="Traz os contatos que o WhatsApp conhece (nome de perfil e o que veio quando o número foi conectado)"
              >
                <i className="bi bi-whatsapp me-1" />
                {importando ? 'Puxando...' : 'Puxar do WhatsApp'}
              </button>

              <label
                className="btn btn-sm"
                style={{ background: '#3D8B37', color: '#fff', border: 'none', cursor: importando ? 'default' : 'pointer' }}
              >
                <i className="bi bi-file-earmark-arrow-up me-1" />
                {importando ? 'Importando...' : 'Escolher arquivo (.csv ou .vcf)'}
                <input
                  type="file"
                  accept=".csv,.vcf,text/csv,text/vcard"
                  disabled={importando}
                  style={{ display: 'none' }}
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; importarArquivo(f); }}
                />
              </label>
              </div>

              {/* Enquanto grava: diz o tamanho do que está indo. Barra de
                  progresso exigiria o servidor mandando notícia no meio do
                  caminho; o número já tira a dúvida de "travou ou é assim?". */}
              {importando && importInfo && (
                <div style={{
                  marginTop: 14, padding: '10px 14px', borderRadius: 10,
                  background: 'rgba(61,139,55,0.08)', border: '1px solid rgba(61,139,55,0.3)',
                  fontSize: '0.86rem', display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <div className="screen-loader__spinner" style={{ width: 18, height: 18, borderWidth: 2, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span>
                      {importInfo.fase === 'lendo' ? (
                        <>Lendo o arquivo ({importInfo.kb.toLocaleString('pt-BR')} KB)…</>
                      ) : (
                        <>
                          Gravando <b>{importInfo.feitos.toLocaleString('pt-BR')}</b> de
                          {' '}<b>{importInfo.total.toLocaleString('pt-BR')}</b> contato(s) —
                          {' '}não feche esta janela.
                        </>
                      )}
                    </span>
                    {/* A barra existe porque o número sozinho não diz se está
                        andando ou parado. Quem importa 3 mil contatos fica
                        minutos olhando pra isto. */}
                    {importInfo.fase === 'gravando' && importInfo.total > 0 && (
                      <div style={{
                        marginTop: 6, height: 6, borderRadius: 999, overflow: 'hidden',
                        background: 'rgba(61,139,55,0.18)',
                      }}>
                        <div style={{
                          height: '100%', borderRadius: 999, background: '#3D8B37',
                          width: `${Math.round((importInfo.feitos / importInfo.total) * 100)}%`,
                          transition: 'width .3s ease',
                        }} />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {importResultado && (
                <div style={{
                  marginTop: 14, padding: '10px 14px', borderRadius: 10,
                  background: 'rgba(61,139,55,0.10)', border: '1px solid rgba(61,139,55,0.35)',
                  fontSize: '0.86rem', color: 'var(--portal-text)',
                }}>
                  <i className="bi bi-check-circle-fill me-2" style={{ color: '#3D8B37' }} />
                  Importação concluída: <b>{importResultado.criados}</b> criados,{' '}
                  <b>{importResultado.atualizados}</b> atualizados,{' '}
                  <b>{importResultado.ignorados}</b> ignorados{' '}
                  (de {importResultado.total} lidos).
                  {importResultado.motivos && importResultado.ignorados > 0 && (
                    <div style={{ marginTop: 8, fontSize: '0.8rem', color: 'var(--portal-muted)' }}>
                      Ignorados por motivo:{' '}
                      {importResultado.motivos.duplicado_no_arquivo > 0 && (
                        <span className="me-2">🔁 {importResultado.motivos.duplicado_no_arquivo} repetidos no arquivo</span>
                      )}
                      {importResultado.motivos.ja_existia > 0 && (
                        <span className="me-2">✓ {importResultado.motivos.ja_existia} já na agenda</span>
                      )}
                      {importResultado.motivos.numero_invalido > 0 && (
                        <span className="me-2" style={{ color: '#c0392b' }}>⚠️ {importResultado.motivos.numero_invalido} número inválido (fixo sem DDD, etc.)</span>
                      )}
                      {importResultado.motivos.sem_numero > 0 && (
                        <span className="me-2">∅ {importResultado.motivos.sem_numero} sem telefone</span>
                      )}
                      {/* Contato que tropeçou na hora de gravar. Antes isso
                          derrubava a importação inteira; agora ele fica pra
                          trás sozinho — e precisa aparecer, senão some calado. */}
                      {importResultado.motivos.erro > 0 && (
                        <span className="me-2" style={{ color: '#c0392b' }}>
                          ✖ {importResultado.motivos.erro} não deram pra gravar
                          {Array.isArray(importResultado.exemplos_erro) && importResultado.exemplos_erro[0]
                            ? ` (ex.: ${importResultado.exemplos_erro[0].nome || importResultado.exemplos_erro[0].numero})`
                            : ''}
                        </span>
                      )}
                    </div>
                  )}
                  {/* Repetido no arquivo com nome diferente: o Fio fica com o
                      primeiro, mas não tem como saber qual é o certo. Mostrar
                      a disputa é o que permite consertar os poucos que doem. */}
                  {Array.isArray(importResultado.nomes_disputados) && importResultado.nomes_disputados.length > 0 && (
                    <details style={{ marginTop: 8, fontSize: '0.8rem' }}>
                      <summary style={{ cursor: 'pointer', color: '#8a6d3b' }}>
                        {importResultado.nomes_disputados.length} número(s) vieram
                        {' '}com mais de um nome — toque no que vale
                      </summary>
                      <div style={{ maxHeight: 200, overflowY: 'auto', marginTop: 6 }}>
                        {importResultado.nomes_disputados.map((d) => (
                          <div key={d.numero} style={{ padding: '6px 0', borderBottom: '1px solid var(--portal-border)' }}>
                            <span style={{ color: 'var(--portal-muted)' }}>{formatNumero(d.numero)}</span>
                            {nomeEscolhido[d.numero] && nomeEscolhido[d.numero] !== 'salvando' ? (
                              <span style={{ marginLeft: 8 }}>
                                <i className="bi bi-check-lg me-1" style={{ color: '#3D8B37' }} />
                                <strong>{nomeEscolhido[d.numero]}</strong>
                              </span>
                            ) : (
                              <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6, marginLeft: 8, verticalAlign: 'middle' }}>
                                {[d.ficou, ...d.outros].filter(Boolean).map((n, i) => (
                                  <button
                                    key={n}
                                    type="button"
                                    disabled={!d.contato_id || nomeEscolhido[d.numero] === 'salvando'}
                                    onClick={() => escolherNome(d, n)}
                                    title={i === 0 ? 'É o que está valendo agora' : 'Usar este nome'}
                                    style={{
                                      fontSize: '0.78rem', padding: '3px 10px', borderRadius: 999, cursor: 'pointer',
                                      border: `1px solid ${i === 0 ? '#3D8B37' : 'var(--portal-border)'}`,
                                      background: i === 0 ? 'rgba(61,139,55,0.14)' : 'transparent',
                                      color: 'var(--portal-text)',
                                    }}
                                  >
                                    {n}{i === 0 ? ' ✓' : ''}
                                  </button>
                                ))}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                  {Array.isArray(importResultado.exemplos_invalidos) && importResultado.exemplos_invalidos.length > 0 && (
                    <details style={{ marginTop: 8, fontSize: '0.8rem' }}>
                      <summary style={{ cursor: 'pointer', color: '#c0392b' }}>
                        Ver números inválidos ({importResultado.exemplos_invalidos.length}) — confira se perdeu algum cliente
                      </summary>
                      <div style={{ maxHeight: 180, overflowY: 'auto', marginTop: 6 }}>
                        {importResultado.exemplos_invalidos.map((x, i) => (
                          <div key={i} style={{ color: 'var(--portal-muted)' }}>
                            {x.nome || '(sem nome)'} — <code>{x.numero || '(vazio)'}</code>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {modalAberto && (
        <div
          onClick={() => { if (!salvando) setModalAberto(false); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(8, 23, 43, 0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            className="content-card"
            // Clique DENTRO não fecha (só o fundo fecha).
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(520px, 96vw)', padding: 0, display: 'flex',
              flexDirection: 'column', overflow: 'hidden',
              // Teto de altura + corpo rolável: sem isso o modal crescia além da
              // tela e a roda do mouse rolava a LISTA atrás dele.
              maxHeight: '92vh',
            }}
          >
            <header style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 18px', borderBottom: '1px solid var(--portal-border)',
            }}>
              <strong>
                <i className={`bi ${editando ? 'bi-pencil' : 'bi-plus-circle'} me-2`} />
                {editando ? 'Editar contato' : 'Novo contato'}
              </strong>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setModalAberto(false)}
                disabled={salvando}
              >
                <i className="bi bi-x-lg" />
              </button>
            </header>

            <div style={{
              padding: 18, display: 'flex', flexDirection: 'column', gap: 12,
              overflowY: 'auto', flex: 1, minHeight: 0,
            }}>
              {erroForm && (
                <div className="feedback-banner feedback-banner--error" style={{ fontSize: '0.85rem' }}>
                  {erroForm}
                </div>
              )}

              <div>
                <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                  Canal (agenda) <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <select
                  className="form-select"
                  value={form.canal_id}
                  onChange={(e) => setForm({ ...form, canal_id: e.target.value })}
                  disabled={!!editando}
                  title={editando
                    ? 'A ficha pertence a esta agenda. Pra ter o contato em outro canal, cadastre nele.'
                    : 'Em qual agenda esse contato entra'}
                >
                  <option value="">Escolha o canal</option>
                  {canais.map((c) => (
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
                </select>
                <div style={{ fontSize: '0.72rem', color: 'var(--portal-muted)', marginTop: 3 }}>
                  O nome e o roteamento abaixo valem só nesta agenda.
                </div>
              </div>

              {!editando && (
                <>
                  <div>
                    <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                      Número (com DDD)
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.numero}
                      onChange={(e) => setForm({ ...form, numero: e.target.value })}
                      placeholder="Ex: 21965868915 ou 5521965868915"
                    />
                  </div>
                </>
              )}

              <div>
                <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                  Nome <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={form.push_name}
                  onChange={(e) => setForm({ ...form, push_name: e.target.value })}
                  placeholder="Como o contato deve aparecer"
                  autoFocus
                />
              </div>

              <div className="row g-3" style={{ margin: 0 }}>
                <div className="col-12 col-sm-6" style={{ paddingLeft: 0 }}>
                  <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                    Filial (opcional)
                  </label>
                  <select
                    className="form-select"
                    value={form.filial}
                    onChange={(e) => setForm({ ...form, filial: e.target.value })}
                  >
                    <option value="">Não informar</option>
                    {form.filial && !filiais.some((f) => f.nome === form.filial) && (
                      <option value={form.filial}>{form.filial}</option>
                    )}
                    {filiais.map((f) => (
                      <option key={f.id || f.nome} value={f.nome}>
                        {f.codigo ? `${f.codigo} - ${f.nome}` : f.nome}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-sm-6" style={{ paddingRight: 0 }}>
                  <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                    Fila padrão (opcional)
                  </label>
                  <select
                    className="form-select"
                    value={form.setor}
                    onChange={(e) => setForm({ ...form, setor: e.target.value, atendente_padrao: '' })}
                    title="Pra qual fila as mensagens desse cliente vão neste canal"
                  >
                    <option value="">Deixar o bot decidir</option>
                    {form.setor && !filasDoCanal.some((f) => f.nome === form.setor) && (
                      <option value={form.setor}>{form.setor}</option>
                    )}
                    {filasDoCanal.map((f) => (
                      <option key={f.id || f.nome} value={f.nome}>{f.nome}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                  Atendente padrão (opcional)
                </label>
                <select
                  className="form-select"
                  value={form.atendente_padrao}
                  onChange={(e) => setForm({ ...form, atendente_padrao: e.target.value })}
                  disabled={!form.setor}
                  title={form.setor
                    ? 'A conversa cai direto nessa pessoa, sem passar pela fila'
                    : 'Escolha antes a fila padrão — o atendente vem dela'}
                >
                  <option value="">Qualquer um da fila</option>
                  {form.atendente_padrao && !atendentesFila.some((a) => a.login === form.atendente_padrao) && (
                    <option value={form.atendente_padrao}>{form.atendente_padrao}</option>
                  )}
                  {atendentesFila.map((a) => (
                    <option key={a.login} value={a.login}>{a.login}</option>
                  ))}
                </select>
                <div style={{ fontSize: '0.72rem', color: 'var(--portal-muted)', marginTop: 3 }}>
                  Mais amplo: só a fila. Mais específico: a pessoa. Sem nada aqui, entra na
                  distribuição normal da fila.
                </div>
              </div>

              {/* Tags: só na edição — contato que está nascendo ainda não tem id
                  pra pendurar etiqueta. */}
              {editando && (
                <div style={{ marginTop: 16 }}>
                  <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>Tags</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                    {tags.filter((t) => t.ativo).map((t) => {
                        const marcada = tagsDoContato.some((x) => x.id === t.id);
                        const cor = t.cor || '#3D8B37';
                        return (
                          <span key={t.id} style={{ display: 'inline-flex', alignItems: 'center' }}>
                            <button
                              type="button"
                              disabled={tagOcupada}
                              onClick={() => alternarTag(t)}
                              title={marcada ? 'Tirar do contato' : 'Pôr no contato'}
                              style={{
                                fontSize: '0.78rem', padding: '4px 10px', borderRadius: 999, cursor: 'pointer',
                                border: `1px solid ${cor}`,
                                background: marcada ? cor : 'transparent',
                                color: marcada ? '#fff' : 'var(--portal-text)',
                              }}
                            >
                              {marcada && <i className="bi bi-check-lg me-1" />}{t.nome}
                            </button>
                            {/* Renomear e excluir moram na própria tag: é onde a
                                pessoa está olhando quando percebe o erro. */}
                            <button type="button" onClick={() => renomearTag(t)} title="Renomear"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--portal-muted)', padding: '0 2px', fontSize: '0.7rem' }}>
                              <i className="bi bi-pencil" />
                            </button>
                            <button type="button" onClick={() => excluirTag(t)} title="Excluir tag"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c0392b', padding: '0 2px', fontSize: '0.7rem' }}>
                              <i className="bi bi-x-lg" />
                            </button>
                          </span>
                        );
                      })}
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    <input
                      className="form-control form-control-sm"
                      placeholder="Nova tag (ex.: Arquiteto)"
                      value={novaTag}
                      maxLength={40}
                      onChange={(e) => setNovaTag(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); criarTag(); } }}
                      style={{ maxWidth: 220 }}
                    />
                    <button type="button" className="btn btn-sm btn-outline-secondary"
                      disabled={!novaTag.trim() || tagOcupada} onClick={criarTag}>
                      Criar e aplicar
                    </button>
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--portal-muted)', marginTop: 4 }}>
                    As tags são suas: outro atendente não vê as suas marcações.
                  </div>
                </div>
              )}

              <label
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, marginTop: 16,
                  padding: '10px 12px', borderRadius: 10,
                  background: 'var(--portal-surface-soft)', border: '1px solid var(--portal-border)',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={!!form.pular_fluxo || !!form.setor || !!form.atendente_padrao}
                  disabled={!!form.setor || !!form.atendente_padrao}
                  onChange={(e) => setForm({ ...form, pular_fluxo: e.target.checked })}
                />
                <span style={{ fontSize: '0.85rem', color: 'var(--portal-text)' }}>
                  Pular o bot
                  <br />
                  <span style={{ fontSize: '0.75rem', color: 'var(--portal-muted)' }}>
                    {(form.setor || form.atendente_padrao)
                      ? 'Já está valendo: com fila ou atendente padrão definido, o Fio sabe pra onde mandar e não pergunta nada ao cliente.'
                      : 'Sem fila nem atendente padrão, marque aqui pra esse cliente não ver o menu — ele cai na fila padrão do canal.'}
                  </span>
                </span>
              </label>

              {/* Só na edição: contato que está nascendo não tem passado. */}
              {editando && (
                <div style={{ marginTop: 14 }}>
                  {historico === null ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => verHistorico(editando)}
                      disabled={carregandoHistorico}
                    >
                      <i className="bi bi-clock-history me-1" />
                      {carregandoHistorico ? 'Buscando…' : 'Ver quem mexeu nesta ficha'}
                    </button>
                  ) : historico.length === 0 ? (
                    <div style={{ fontSize: '0.8rem', color: 'var(--portal-muted)' }}>
                      Nenhuma alteração registrada. O histórico começou a ser gravado agora —
                      o que mudou antes disso não foi guardado.
                    </div>
                  ) : (
                    <div style={{
                      border: '1px solid var(--portal-border)', borderRadius: 10,
                      maxHeight: 220, overflowY: 'auto',
                    }}>
                      {historico.map((l) => (
                        <div
                          key={l.id}
                          style={{
                            padding: '8px 12px', fontSize: '0.78rem',
                            borderBottom: '1px solid var(--portal-border)',
                          }}
                        >
                          <div style={{ color: 'var(--portal-text)' }}>
                            <strong>{ROTULO_CAMPO[l.campo] || l.campo}</strong>
                            {': '}
                            {valorLegivel(l, 'antes')}
                            {' → '}
                            <strong>{valorLegivel(l, 'depois')}</strong>
                          </div>
                          <div style={{ color: 'var(--portal-muted)', marginTop: 2 }}>
                            {l.por || 'automático'}
                            {' · '}
                            {ROTULO_ORIGEM[l.origem] || l.origem || 'origem não registrada'}
                            {' · '}
                            {new Date(l.quando).toLocaleString('pt-BR')}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <footer style={{
              padding: '12px 18px', borderTop: '1px solid var(--portal-border)',
              display: 'flex', justifyContent: 'flex-end', gap: 8,
            }}>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setModalAberto(false)}
                disabled={salvando}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-sm"
                onClick={salvar}
                disabled={salvando}
                style={{ background: '#3D8B37', color: '#fff', border: 'none' }}
              >
                <i className="bi bi-check2 me-1" />
                {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
